import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  Circle,
  RefreshCw,
  XCircle,
} from "lucide-react";

import { Loading } from "@pages/v2/components/layout/Loading";
import { formatAmount } from "@pages/v2/queries/transactions/format";
import {
  fetchTransactionDetail,
  retrySettle,
  voidTransaction,
} from "@pages/v2/queries/transactions/transactionQueries";
import type {
  TransactionDetailResponse,
  TransactionLogEntry,
  TransactionLogEvent,
} from "@pages/v2/queries/transactions/types";

const eventIcon = (event: TransactionLogEvent) => {
  switch (event) {
    case "settled":
    case "voided":
      return <CheckCircle2 size={16} className="text-green-600" />;
    case "settlement_failed":
    case "void_failed":
    case "checkout_error":
      return <XCircle size={16} className="text-red-600" />;
    case "authorized":
    default:
      return <Circle size={16} className="text-gray-400" />;
  }
};

const TransactionDetail = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<TransactionDetailResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [actionPending, setActionPending] = useState(false);
  const [actionMessage, setActionMessage] = useState<
    { kind: "success" | "error"; text: string } | undefined
  >();

  const hasPriorSettleAttempt = useMemo(
    () => data?.log.some((e) => e.event === "settlement_failed") ?? false,
    [data],
  );

  const load = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    setError(undefined);
    try {
      const result = await fetchTransactionDetail(transactionId);
      setData(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => {
    load();
  }, [load]);

  const onVoid = async () => {
    if (!transactionId) return;
    const reason = window.prompt(
      "Reason for voiding this transaction? (required)",
    );
    if (!reason) return;
    setActionPending(true);
    setActionMessage(undefined);
    try {
      const res = await voidTransaction(transactionId, reason);
      if (res.success) {
        setActionMessage({ kind: "success", text: "Transaction voided." });
        await load();
      } else {
        setActionMessage({
          kind: "error",
          text: res.message ?? "Void failed.",
        });
      }
    } catch (err) {
      setActionMessage({ kind: "error", text: (err as Error).message });
    } finally {
      setActionPending(false);
    }
  };

  const onRetrySettle = async () => {
    if (!transactionId) return;
    const confirmText = hasPriorSettleAttempt
      ? "Retry settlement for this transaction?"
      : "Submit this transaction for settlement?";
    if (!window.confirm(confirmText)) return;
    setActionPending(true);
    setActionMessage(undefined);
    try {
      const res = await retrySettle(transactionId);
      if (res.success) {
        setActionMessage({
          kind: "success",
          text: res.message ?? "Settlement succeeded.",
        });
        await load();
      } else {
        setActionMessage({
          kind: "error",
          text: res.message ?? "Retry failed.",
        });
        // Still refetch so the failure shows up in the log timeline.
        await load();
      }
    } catch (err) {
      setActionMessage({ kind: "error", text: (err as Error).message });
    } finally {
      setActionPending(false);
    }
  };

  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 bg-transparent text-sm text-gray-600 hover:text-gray-900 focus:outline-none"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="rounded-md bg-red-50 p-4 text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { transaction, log } = data;
  const isAuthorized = transaction.status === "authorized";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 bg-transparent text-sm text-gray-600 hover:text-gray-900 focus:outline-none"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-2">
          {isAuthorized && (
            <>
              <button
                type="button"
                onClick={onRetrySettle}
                disabled={actionPending}
                className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw size={14} />{" "}
                {hasPriorSettleAttempt ? "Retry settle" : "Settle"}
              </button>
              <button
                type="button"
                onClick={onVoid}
                disabled={actionPending}
                className="flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                <Ban size={14} /> Void
              </button>
            </>
          )}
        </div>
      </div>

      {actionMessage && (
        <div
          className={
            actionMessage.kind === "success"
              ? "rounded-md bg-green-50 p-3 text-sm text-green-700"
              : "rounded-md bg-red-50 p-3 text-sm text-red-700"
          }
        >
          {actionMessage.text}
        </div>
      )}

      <section className="rounded-md border border-gray-200 bg-white p-4">
        <h3 className="text-lg font-semibold mb-3">Transaction</h3>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <DtDd label="ID" value={transaction.transactionId} />
          <DtDd
            label="Status"
            value={<StatusPill status={transaction.status} />}
          />
          <DtDd
            label="Amount"
            value={formatAmount(transaction.amount, transaction.currency)}
          />
          <DtDd label="Purchase option" value={transaction.purchaseOption} />
          <DtDd label="Email" value={transaction.email} />
          <DtDd
            label="Customer ID"
            value={
              transaction.customerId ? (
                <Link
                  className="text-blue-600 hover:underline"
                  to={`/users/${transaction.customerId}`}
                >
                  {transaction.customerId}
                </Link>
              ) : (
                "—"
              )
            }
          />
          <DtDd
            label="Authorized at"
            value={formatUtc(transaction.authorizedAt)}
          />
          <DtDd
            label="Settled at"
            value={
              transaction.settledAt ? formatUtc(transaction.settledAt) : "—"
            }
          />
          {transaction.voidedAt && (
            <DtDd label="Voided at" value={formatUtc(transaction.voidedAt)} />
          )}
          {transaction.voidReason && (
            <DtDd label="Void reason" value={transaction.voidReason} />
          )}
        </dl>
      </section>

      <section className="rounded-md border border-gray-200 bg-white p-4">
        <h3 className="text-lg font-semibold mb-3">Timeline</h3>
        {log.length === 0 ? (
          <p className="text-sm text-gray-500">No log entries.</p>
        ) : (
          <ol className="flex flex-col gap-3">
            {log.map((entry) => (
              <TimelineEntry key={entry.id} entry={entry} />
            ))}
          </ol>
        )}
      </section>
    </div>
  );
};

// Transaction timestamps render in UTC with an explicit suffix (OMG-2324):
// the list's day filter brackets UTC days, so the displayed day must be the
// same day the filter selected — local rendering made a 00:16 UTC transaction
// read as the previous day for every admin west of UTC.
const formatUtc = (iso: string): string =>
  `${new Date(iso).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  })} UTC`;

const DtDd = ({ label, value }: { label: string; value: ReactNode }) => (
  <>
    <dt className="text-gray-500">{label}</dt>
    <dd className="text-gray-900">{value}</dd>
  </>
);

const StatusPill = ({ status }: { status: string }) => {
  const color =
    status === "settled"
      ? "bg-green-100 text-green-700"
      : status === "voided"
        ? "bg-gray-200 text-gray-700"
        : status === "settlement_declined" || status === "gateway_rejected"
          ? "bg-red-100 text-red-700"
          : status === "expired"
            ? "bg-orange-100 text-orange-700"
            : "bg-yellow-100 text-yellow-800";
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
    >
      {status}
    </span>
  );
};

const TimelineEntry = ({ entry }: { entry: TransactionLogEntry }) => (
  <li className="flex gap-3">
    <div className="mt-0.5">{eventIcon(entry.event)}</div>
    <div className="flex-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-gray-900">{entry.event}</span>
        <span className="text-xs text-gray-500">
          {formatUtc(entry.createdAt)}
        </span>
      </div>
      <div className="text-xs text-gray-600">
        by {entry.actor}
        {entry.actorId && ` (${entry.actorId})`}
      </div>
      {entry.metadata && Object.keys(entry.metadata).length > 0 && (
        <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-700">
          {JSON.stringify(entry.metadata, null, 2)}
        </pre>
      )}
    </div>
  </li>
);

export default TransactionDetail;
